/**
 * @file blendMath.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-25
 * @description The blend arithmetic, in one place. Pure: no I/O, no database,
 *              no dates, no randomness. Everything it needs is passed in.
 *
 *              THIS FILE EXISTS TWICE:
 *                services/blendMath.js          (server, CommonJS, authoritative)
 *                client/src/utils/blendMath.js  (browser, ESM, GENERATED)
 *
 *              Everything down to the exports footer is shared verbatim; only
 *              the footer differs, because everything under client/src is parsed
 *              as an ES module and a module.exports there throws at runtime.
 *
 *              Both copies are needed. The grid computes live while the user
 *              types — a round-trip per keystroke is not an option — and the
 *              server must never trust a client-supplied figure on an export or
 *              a quote. Do NOT require() across the boundary: docker-compose
 *              bind-mounts services/ and routes/ for ./deploy.sh --no-build but
 *              deliberately not client/, so a --no-build deploy would ship a
 *              maths change server-side only and leave the browser computing
 *              something different.
 *
 *              scripts/check-blendmath-sync.js compares the shared bodies and
 *              deploy.sh refuses to run when they differ. ANY CHANGE HERE NEEDS
 *              A FULL ./deploy.sh, never --no-build. Edit THIS file, then run:
 *                node scripts/check-blendmath-sync.js --fix
 *
 * The arithmetic replicates EA Blend Calculator.xlsx exactly:
 *
 *   nutrient = SUMPRODUCT(nutrient_column, inclusion_column) / 100
 *   cost/T   = SUMPRODUCT(price_column,    inclusion_column) / 100
 *   delivered = cost/T + a flat GBP/T band uplift
 *
 * Note the divisor: 100, not the actual inclusion total. That is what the
 * workbook does, and changing it would silently alter every figure the office
 * has ever quoted. It also means a blend totalling 98% understates every
 * nutrient and the cost by 2% with no visible warning — which is why summary()
 * reports `isBalanced` and callers must refuse to export or quote when it's
 * false.
 */

// A blend is balanced when its inclusions total 100 within this tolerance.
const TOLERANCE = 0.01;

// The threshold below which an ingredient doesn't PRINT, matching the workbook's
// IF(quantity > 0.01, …). Deliberately separate from TOLERANCE and deliberately
// NOT applied to the arithmetic: the workbook's SUMPRODUCT includes every row
// regardless of size, and it is only the printable sheet that hides the tiny
// ones. Filtering the maths too would make a blend's cost disagree with the sum
// of the inclusions it says it contains.
const PRINT_THRESHOLD = 0.01;

/** Finite number or null. Strings from inputs are coerced; '' and '*' are null. */
function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalise the raw as-at data into the lookup the rest of this module uses.
 *
 * @param {Array}  materials  [{ id, key, name, category, sort_order }]
 * @param {Array}  costRows   [{ material_id, price_column_id, cost_per_tonne }]
 * @param {Array}  nutrientRows [{ material_id, values }]
 * @param {Array}  columns    [{ id, key, label, column_kind, sort_order }]
 * @param {Array}  nutrientComponents [{ key, label, unit, decimals, basis }]
 * @returns {{ materials, costs, nutrients, columns, nutrientComponents }}
 */
function buildBook(materials, costRows, nutrientRows, columns, nutrientComponents = []) {
  const byId = new Map();
  const byKey = new Map();
  for (const m of materials || []) {
    const entry = { ...m, costs: {}, nutrients: {} };
    byId.set(m.id, entry);
    byKey.set(m.key, entry);
  }

  const columnKeyById = new Map((columns || []).map((c) => [c.id, c.key]));

  for (const row of costRows || []) {
    const m = byId.get(row.material_id);
    const colKey = columnKeyById.get(row.price_column_id);
    if (!m || !colKey) continue;
    // Explicit null is meaningful: "asked, not quoted". Kept as null so cost()
    // can report it as missing rather than treating it as free.
    m.costs[colKey] = toNumber(row.cost_per_tonne);
  }

  for (const row of nutrientRows || []) {
    const m = byId.get(row.material_id);
    if (!m) continue;
    const values = row.values || {};
    for (const [key, raw] of Object.entries(values)) {
      const n = toNumber(raw);
      if (n !== null) m.nutrients[key] = n;
    }
  }

  return {
    materials: byKey,
    columns: (columns || []).slice().sort((a, b) => a.sort_order - b.sort_order),
    // Only fresh-basis components may be averaged. A dry-matter figure needs
    // SUM(incl x dm x nut) / SUM(incl x dm); averaging it directly is wrong, and
    // wrong in a way that looks entirely plausible on a printed sheet.
    nutrientComponents: (nutrientComponents || []).filter((c) => c.basis === 'fresh')
  };
}

/**
 * Raw sum of inclusions. No rounding — round at render, never mid-calculation.
 * @param {Object} materialValues { materialKey: inclusionPct }
 */
function total(materialValues) {
  let sum = 0;
  for (const raw of Object.values(materialValues || {})) {
    const n = toNumber(raw);
    if (n !== null) sum += n;
  }
  return sum;
}

/**
 * The materials contributing to the blend, largest inclusion first.
 *
 * Everything with a non-zero inclusion counts, however small — see
 * PRINT_THRESHOLD. Only an unset or exactly-zero row drops out.
 */
function activeMaterials(materialValues) {
  return Object.entries(materialValues || {})
    .map(([key, raw]) => ({ key, inclusion: toNumber(raw) }))
    .filter((x) => x.inclusion !== null && x.inclusion !== 0)
    .sort((a, b) => b.inclusion - a.inclusion);
}

/** The materials that appear on a printed sheet: non-trivial inclusions only. */
function printableMaterials(materialValues) {
  return activeMaterials(materialValues)
    .filter((x) => Math.abs(x.inclusion) > PRINT_THRESHOLD);
}

/**
 * Weighted analysis. One entry per fresh-basis component for which at least one
 * contributing material has a value.
 *
 * A material with no figure for a nutrient contributes nothing — which is the
 * workbook's behaviour (a blank cell in SUMPRODUCT is zero) but understates the
 * result, so `partial` lists the components where that happened and the caller
 * should say so on screen.
 *
 * @returns {{ values: Object, partial: Array<{key, missing: string[]}> }}
 */
function nutrients(materialValues, book) {
  const rows = activeMaterials(materialValues);
  const values = {};
  const partial = [];

  for (const component of book.nutrientComponents) {
    let sum = 0;
    let contributed = false;
    const missing = [];

    for (const row of rows) {
      const material = book.materials.get(row.key);
      if (!material) continue;
      const nutrient = material.nutrients[component.key];
      if (nutrient === undefined) {
        missing.push(row.key);
        continue;
      }
      sum += row.inclusion * nutrient;
      contributed = true;
    }

    if (!contributed) continue;              // nothing measures this nutrient
    values[component.key] = sum / 100;
    if (missing.length) partial.push({ key: component.key, missing });
  }

  return { values, partial };
}

/**
 * Raw-material cost per tonne for one price column.
 *
 * `missing` lists materials with no price in this column. They contribute
 * nothing to the total, so the figure is an UNDERSTATEMENT and must not be
 * quoted — callers refuse to export while it is non-empty. Never coerce a
 * missing price to zero.
 *
 * @returns {{ cost: number, missing: string[] }}
 */
function cost(materialValues, book, columnKey) {
  const rows = activeMaterials(materialValues);
  let sum = 0;
  const missing = [];

  for (const row of rows) {
    const material = book.materials.get(row.key);
    if (!material) { missing.push(row.key); continue; }
    const price = material.costs[columnKey];
    if (price === undefined || price === null) { missing.push(row.key); continue; }
    sum += row.inclusion * price;
  }

  return { cost: sum / 100, missing };
}

/**
 * Delivered price per tonne for each haulage band: raw-material cost plus the
 * band's flat uplift.
 * @param {number} rawMaterialCost
 * @param {Array}  bands [{ key, label, uplift_per_tonne }]
 */
function delivered(rawMaterialCost, bands) {
  const out = {};
  for (const band of bands || []) {
    const uplift = toNumber(band.uplift_per_tonne) ?? 0;
    out[band.key] = rawMaterialCost + uplift;
  }
  return out;
}

/**
 * Per-material cost contribution: what each material adds to the GBP/T, and its
 * share of the total. The workbook buried this in columns L/M; surfacing it is
 * the fastest way to see what is driving a price.
 */
function contributions(materialValues, book, columnKey) {
  const rows = activeMaterials(materialValues);
  const out = [];
  let totalCost = 0;

  for (const row of rows) {
    const material = book.materials.get(row.key);
    const price = material ? material.costs[columnKey] : undefined;
    const amount = (price === undefined || price === null) ? null : (row.inclusion * price) / 100;
    if (amount !== null) totalCost += amount;
    out.push({
      key: row.key,
      name: material ? material.name : row.key,
      inclusion: row.inclusion,
      pricePerTonne: price ?? null,
      contribution: amount
    });
  }

  return out.map((row) => ({
    ...row,
    shareOfCost: (row.contribution !== null && totalCost > 0)
      ? (row.contribution / totalCost) * 100
      : null
  }));
}

/**
 * Redistribute the shortfall to 100% across the unlocked materials, pro rata to
 * their current share.
 *
 *  - Locked rows never move.
 *  - Only rows already carrying an inclusion take a share; a 0% row stays 0
 *    (a blend shouldn't silently acquire an ingredient).
 *  - Each result is rounded to `decimals`, then the rounding residue is pushed
 *    onto the largest unlocked row so the total is EXACTLY 100 rather than
 *    99.99. A quote that fails its own balance check is worse than useless.
 *  - With exactly one unlocked row, it absorbs the entire shortfall. That is the
 *    "barley is the filler" case, and it is what people actually want.
 *
 * With nothing locked and no zero rows this is exactly a pro-rata rescale of the
 * whole recipe, so there is no separate "normalise" operation — one button, one
 * predictable behaviour. Locking is how you say "not that one".
 *
 * Returns a new object; never mutates the input. Returns it unchanged when
 * there is nothing to distribute across.
 */
/**
 * @param {string} [mode='proportional']
 *   'proportional' — each unlocked material gives or takes in proportion to its
 *   size, so the ratios between them survive the change.
 *   'equal' — every unlocked material moves by the same absolute amount, which
 *   is what you want when they are interchangeable fillers. A material that
 *   would go below zero stops at zero and its share is redistributed.
 */
function balance(materialValues, lockedKeys = [], decimals = 2, mode = 'proportional') {
  const locked = new Set(lockedKeys || []);
  const values = { ...(materialValues || {}) };

  const adjustable = Object.keys(values).filter((key) => {
    if (locked.has(key)) return false;
    const n = toNumber(values[key]);
    return n !== null && Math.abs(n) > TOLERANCE;
  });

  if (!adjustable.length) return values;

  const currentTotal = total(values);
  const shortfall = 100 - currentTotal;
  if (Math.abs(shortfall) <= TOLERANCE / 10) return values;

  const adjustableTotal = adjustable.reduce((s, key) => s + toNumber(values[key]), 0);
  if (adjustableTotal <= 0) return values;

  const factor = 10 ** decimals;

  if (mode === 'equal') {
    // Repeat rather than divide once: a material that hits zero cannot absorb
    // its full share, and the remainder has to go somewhere or the blend
    // silently fails to reach 100.
    let remaining = shortfall;
    let live = adjustable.slice();
    for (let pass = 0; pass < 12 && Math.abs(remaining) > TOLERANCE / 10 && live.length; pass += 1) {
      const each = remaining / live.length;
      const next = [];
      for (const key of live) {
        const current = toNumber(values[key]);
        const applied = current + each < 0 ? -current : each;
        values[key] = Math.round((current + applied) * factor) / factor;
        remaining -= applied;
        if (toNumber(values[key]) > TOLERANCE) next.push(key);
      }
      live = next;
    }
  } else {
    for (const key of adjustable) {
      const current = toNumber(values[key]);
      const share = current / adjustableTotal;
      values[key] = Math.round((current + shortfall * share) * factor) / factor;
    }
  }

  // Absorb the rounding residue on the largest unlocked row.
  const residue = Math.round((100 - total(values)) * factor) / factor;
  if (residue !== 0) {
    const largest = adjustable.reduce((a, b) =>
      toNumber(values[a]) >= toNumber(values[b]) ? a : b);
    values[largest] = Math.round((toNumber(values[largest]) + residue) * factor) / factor;
  }

  return values;
}

/**
 * Move one material to `next`, sourcing the difference from somewhere sensible.
 *
 * The free space is a participant, and it is used first. Growing a material eats
 * whatever is unallocated before it touches anything else; shrinking one hands
 * space back to the gap before it fattens its neighbours. Two things follow that
 * a strictly zero-sum rule cannot give you: dragging into the gap closes it, and
 * every change is reversible — move up then down by the same amount and you are
 * exactly where you started.
 *
 * Locked materials are never touched, and neither is the one being moved. When
 * the unlocked pool runs dry the move is truncated rather than forcing the blend
 * past 100%.
 *
 * @param {Object} values
 * @param {string[]} lockedKeys
 * @param {string} key      the material being moved
 * @param {number} next     where it should end up
 * @param {string} [mode='proportional'] how the rest give way — see balance()
 * @param {number} [decimals=2]
 * @returns {Object} a new values object
 */
function setWithRedistribution(values, lockedKeys, key, next, mode = 'proportional', decimals = 2) {
  const locked = new Set(lockedKeys || []);
  if (locked.has(key)) return values;

  const factor = 10 ** decimals;
  const out = { ...(values || {}) };
  const current = toNumber(out[key]) || 0;
  const target = Math.max(0, Math.min(100, next));
  let delta = target - current;
  if (Math.abs(delta) <= TOLERANCE / 10) return values;

  const gap = 100 - total(out);
  // Growing takes from the gap; shrinking gives back to it. Clamped so we never
  // manufacture a gap larger than the bar.
  const fromGap = delta > 0 ? Math.min(delta, Math.max(0, gap))
                            : Math.max(delta, -(100 - Math.max(0, gap)));
  delta -= fromGap;

  let fromPool = 0;
  if (Math.abs(delta) > TOLERANCE / 10) {
    const pool = Object.keys(out).filter((k) => k !== key && !locked.has(k));
    let remaining = delta;
    for (let pass = 0; pass < 12 && Math.abs(remaining) > TOLERANCE / 10; pass += 1) {
      // Taking: only materials with something left. Giving back: everyone,
      // including those sitting at zero.
      // Bound per pass: the closure below runs synchronously within this
      // iteration, but reading the mutable `remaining` from inside it is the
      // kind of thing that quietly breaks when someone makes this async later.
      const giving = remaining < 0;
      const live = pool.filter((k) => giving || (toNumber(out[k]) || 0) > TOLERANCE);
      if (!live.length) break;
      const liveTotal = live.reduce((sum, k) => sum + (toNumber(out[k]) || 0), 0);
      // Proportional needs something to be proportional TO. Giving back to a
      // pool that is entirely at zero has no ratio to preserve, so it falls back
      // to an equal split rather than stalling and losing the remainder.
      const byShare = mode !== 'equal' && liveTotal > TOLERANCE;
      for (const k of live) {
        const held = toNumber(out[k]) || 0;
        const share = byShare ? held / liveTotal : 1 / live.length;
        let give = remaining * share;
        if (held - give < 0) give = held;
        out[k] = Math.round((held - give) * factor) / factor;
        remaining -= give;
        fromPool += give;
      }
    }
  }

  out[key] = Math.round((current + fromGap + fromPool) * factor) / factor;
  return out;
}

/**
 * Everything the calculator screen and the export need, in one pass.
 *
 * @param {Object} materialValues { materialKey: inclusionPct }
 * @param {Object} book            from buildBook()
 * @param {Array}  bands           haulage bands
 * @param {Object} [options.spec]  { targets: { componentKey: {min,max} } }
 * @param {string[]} [options.columnKeys] price columns to cost; defaults to all
 */
function summary(materialValues, book, bands = [], options = {}) {
  const inclusionTotal = total(materialValues);
  const rows = activeMaterials(materialValues);

  const columnKeys = options.columnKeys
    || book.columns.map((c) => c.key);

  const analysis = nutrients(materialValues, book);

  const pricing = {};
  for (const key of columnKeys) {
    const { cost: rawMaterialCost, missing } = cost(materialValues, book, key);
    pricing[key] = {
      rawMaterialCost,
      missing,
      delivered: delivered(rawMaterialCost, bands),
      contributions: contributions(materialValues, book, key)
    };
  }

  // Materials in the blend that the book has never heard of — a draft saved
  // before a material was deactivated, or a stale client bundle.
  const unknown = rows.filter((r) => !book.materials.has(r.key)).map((r) => r.key);

  return {
    total: inclusionTotal,
    remaining: 100 - inclusionTotal,
    isBalanced: Math.abs(inclusionTotal - 100) <= TOLERANCE,
    materialCount: rows.length,
    nutrients: analysis.values,
    partialNutrients: analysis.partial,
    unknownMaterials: unknown,
    pricing,
    spec: options.spec ? evaluateSpec(analysis.values, options.spec) : null
  };
}

/**
 * Check computed nutrients against a target spec.
 *
 * `status` is 'pass' | 'fail' | 'unknown' per component — 'unknown' when the
 * blend has no figure for a targeted nutrient, which is NOT a pass.
 */
function evaluateSpec(nutrientValues, spec) {
  const targets = (spec && spec.targets) || {};
  const results = {};
  let pass = true;

  for (const [key, target] of Object.entries(targets)) {
    const value = nutrientValues[key];
    const min = toNumber(target && target.min);
    const max = toNumber(target && target.max);

    if (value === undefined) {
      results[key] = { status: 'unknown', value: null, min, max };
      pass = false;
      continue;
    }
    const underMin = min !== null && value < min;
    const overMax = max !== null && value > max;
    const ok = !underMin && !overMax;
    if (!ok) pass = false;
    results[key] = { status: ok ? 'pass' : 'fail', value, min, max, underMin, overMax };
  }

  return { pass, results };
}

// ─── exports ─────────────────────────────────────────────────────────────────
// GENERATED by scripts/check-blendmath-sync.js — do not edit this file.
// Everything above this line is shared verbatim with services/blendMath.js,
// which is the authoritative copy. Edit that, then run:
//   node scripts/check-blendmath-sync.js --fix
//
// ESM rather than CommonJS because everything under client/src is parsed as an
// ES module; a module.exports here throws at runtime and takes the page down.
export {
  TOLERANCE,
  PRINT_THRESHOLD,
  toNumber,
  buildBook,
  total,
  activeMaterials,
  printableMaterials,
  nutrients,
  cost,
  delivered,
  contributions,
  balance,
  setWithRedistribution,
  summary,
  evaluateSpec
};
